import React, {useState} from "react";
import {observer} from "mobx-react-lite";
import classNames from "classnames";
import {CheckoutEventsItem, CheckoutEventsTimePeriodInterval} from "@paddle/paddle-js";
import intl from "react-intl-universal";

import {PaymentGateway} from "../../../types";

import {outputPriceWithCurrency} from "../../../../utils/outputPriceWithCurrency";
import {LoadingIcon} from "../../../../ui/components/icons/LoadingIcon";
import {HorizontalExpandIcon} from "../../../../ui/components/icons/HorizontalExpandIcon";
import {getPriceItemById, SUBSCRIPTION_ITEMS} from "../../../subscription_plans";
import {getLocalItemName} from "../../getLocalItemName";


import './PriceDetails.css';


export interface PriceDetailsProps {
    paymentGateway: PaymentGateway;
    isUpgrade?: boolean;
}


export const PriceDetails = observer((
    {
        paymentGateway,
        isUpgrade = false,
    }: PriceDetailsProps) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const priceCurrency = paymentGateway.priceCurrency;
    const includeVAT = paymentGateway.canShowTax;

    if (paymentGateway.isLoading || !paymentGateway.totals) {
        return (
            <div className="PriceDetails PriceDetails-loader">
                <LoadingIcon
                    className='btn__loading'
                    type={'primary'}
                    size={null}
                />
            </div>
        )
    }

    let periodText = (
        paymentGateway.checkoutInput.period === 'yearly'
            ? intl.get('common.year').d('year')
            : intl.get('common.month').d('month')
    );

    return (
        <div className="PriceDetails">
            {isUpgrade ? (
                <div className="PriceDetails-main PriceDetails-main--upgrade"
                     onClick={() => setIsExpanded(!isExpanded)}>
                    <div className="body-medium-bold">
                        {intl.get('checkout.price-details-update').d('Next billing period')}
                    </div>
                    <div className="PriceDetails-total">
                        <span className="PriceDetails-total-value body-large">
                            {outputPriceWithCurrency(paymentGateway.totals.total, priceCurrency)}
                        </span>
                        <span className="PriceDetails-description body-large">
                            /{periodText} {
                            includeVAT
                                ? `(${intl.get('checkout.price-inc-vat').d('inc. VAT')})`
                                : `(${intl.get('checkout.price-excl-vat').d('excl. tax')})`
                        }
                        </span>
                        <HorizontalExpandIcon className="PriceDetails-expand" isExpanded={isExpanded}/>
                    </div>
                </div>
            ) : (
                <div className="PriceDetails-main" onClick={() => setIsExpanded(!isExpanded)}>
                    <div>
                       <span className="PriceDetails-total-value headline-large-bold">
                           {outputPriceWithCurrency(paymentGateway.totals.total, priceCurrency)}
                       </span>
                        <span className="PriceDetails-description body-large">
                           /{periodText} {
                            includeVAT
                                ? `(${intl.get('checkout.price-inc-vat').d('inc. VAT')})`
                                : `(${intl.get('checkout.price-excl-vat').d('excl. tax')})`
                        }
                       </span>
                    </div>
                    <HorizontalExpandIcon className="PriceDetails-expand" isExpanded={isExpanded}/>
                </div>
            )}
            <PriceDetailsBreakdown
                paymentGateway={paymentGateway}
                includeVAT={includeVAT}
                isUpgrade={isUpgrade}
                isExpanded={isExpanded}
            />
        </div>
    )
});

export interface PriceDetailsBreakdownProps {
    paymentGateway: PaymentGateway;
    includeVAT?: boolean;
    isUpgrade?: boolean;
    isExpanded?: boolean;
    standalone?: boolean;
}

export const PriceDetailsBreakdown = observer((
    {
        paymentGateway,
        includeVAT,
        isUpgrade = false,
        isExpanded = true,
        standalone = false,
    }: PriceDetailsBreakdownProps) => {
    const priceCurrency = paymentGateway.priceCurrency;

    return (
        <div className={classNames({
            'PriceDetails-check': true,
            'PriceDetails-check--expanded': isExpanded,
            'PriceDetails-check--upgrade': isUpgrade,
            'PriceDetails-check--standalone': standalone,
        })}>
            {
                paymentGateway.items.map(item => (
                    <PriceItem
                        key={item.price_id}
                        item={item}
                        priceCurrency={priceCurrency}
                    />
                ))
            }
            {
                includeVAT ? (
                        <div className="pdl-detail-row pdl-check-item pdl-tax-row body-large-bold">
                            <div>
                                {intl.get('checkout.tax').d('Tax')}
                            </div>
                            <div>
                                ${paymentGateway.totals.tax}
                            </div>
                        </div>
                    )
                    : null
            }
            <div className="pdl-check-divider"/>
            <div className="pdl-check-item pdl-check-item--total body-large-bold">
                <div>
                    {intl.get('checkout.check.total').d('Total')}
                </div>
                <div>
                    {outputPriceWithCurrency(paymentGateway.totals.total, priceCurrency)}
                </div>
            </div>
        </div>
    );
});

interface PaddlePriceItemProps {
    item: CheckoutEventsItem;
    priceCurrency: 'RUB' | 'USD';
}

const PriceItem = (
    {
        item,
        priceCurrency,
    }: PaddlePriceItemProps
) => {
    const period = item.billing_cycle!.interval;

    let perMonthText = intl.get('common.1month').d('1 month');
    let costPerMonth = item.totals.subtotal;
    if (period === CheckoutEventsTimePeriodInterval.YEAR) {
        costPerMonth = costPerMonth / 12;
        perMonthText = intl.get('common.12months').d('12 months');
    }
    let calculation: string;
    let planName = getLocalItemName(item);

    const priceItem = getPriceItemById(item.price_id);

    switch (priceItem) {
        case SUBSCRIPTION_ITEMS.LOCATION_SLOT:
            const perLocationText = intl.get('common.n-locations', {count: item.quantity}).d('{count, plural, one {# location} other {# locations}}');
            calculation = `${outputPriceWithCurrency(costPerMonth / item.quantity, priceCurrency)} x ${perLocationText} х ${perMonthText}`;
            break;
        case SUBSCRIPTION_ITEMS.USER_SEAT:
            const perUserText = intl.get('common.n-users', {count: item.quantity}).d('{count, plural, one {# user} other {# users}}');
            calculation = `${outputPriceWithCurrency(costPerMonth / item.quantity, priceCurrency)} x ${perUserText} х ${perMonthText}`;
            break;
        case SUBSCRIPTION_ITEMS.STANDARD:
        default:
            calculation = `${outputPriceWithCurrency(costPerMonth, priceCurrency)} х ${perMonthText}`;
            break;
    }

    return (
        <div className="pdl-detail-row body-large">
            <div className="pdl-check-item body-large-bold">
                <div>
                    {planName}
                </div>
                <div>
                    {outputPriceWithCurrency(item.totals.subtotal, priceCurrency)}
                </div>
            </div>
            <div>
                {calculation}
            </div>
        </div>
    )
}
